import { useState } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  PlatformFeesTable,
  PlatformFeesExplainer,
  type FeeRow,
} from '@/components/PlatformFeesInfo'
import {
  ArrowUpRight,
  CalendarClock,
  CalendarSearch,
  CheckCircle2,
  Download,
  Info,
  Wallet,
  Receipt,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

// ── Helpers ──

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(value: string | null, pattern = 'dd/MM/yyyy'): string {
  if (!value) return '-'
  return format(parseISO(value), pattern, { locale: ptBR })
}

const METHOD_LABELS: Record<string, string> = {
  pix: 'PIX',
  credit_card: 'Crédito',
  debit_card: 'Débito',
  bank_transfer: 'Boleto',
  cash: 'Dinheiro',
}

function methodLabel(method: string, installments: number): string {
  const base = METHOD_LABELS[method] ?? method
  if (method === 'credit_card' && installments > 1)
    return `${base} ${installments}x`
  return base
}

// ── Types ──

interface School {
  id: number
  name: string
}

interface PayoutPayment {
  id: number
  student_name: string
  package_name: string
  paid_at: string | null
  amount: number
  fee_amount: number
  fee_rate: number
  net_amount: number
  payment_method: string
  installment_count: number
}

interface NextPayout {
  period_start: string
  period_end: string
  payout_date: string
  gross_amount: number
  fees_amount: number
  net_amount: number
  transactions_count: number
  carried_over: { gross_amount: number; transactions_count: number }
  payments: PayoutPayment[]
}

interface MethodRow {
  payment_method: string
  transactions_count: number
  gross_amount: number
  fees_amount: number
  net_amount: number
}

interface PeriodReport {
  from: string
  to: string
  gross_amount: number
  fees_amount: number
  net_amount: number
  transactions_count: number
  by_method: MethodRow[]
  payments: PayoutPayment[]
}

interface HistoryEntry {
  id: number
  period_label: string
  period_start: string
  period_end: string
  payout_date: string
  gross_amount: number
  fees_amount: number
  net_amount: number
  transactions_count: number
  status: string
  paid_at: string | null
}

interface Props {
  school: School
  next_payout: NextPayout
  period_report: PeriodReport
  history: HistoryEntry[]
  fee_table: FeeRow[]
}

// ── Sub-componentes ──

function PaymentsTable({ payments }: { payments: PayoutPayment[] }) {
  if (payments.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Nenhum pagamento por aqui ainda.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Aluno</TableHead>
            <TableHead>Pacote</TableHead>
            <TableHead>Pago em</TableHead>
            <TableHead>Forma</TableHead>
            <TableHead className="text-right">Bruto</TableHead>
            <TableHead className="text-right">Taxa</TableHead>
            <TableHead className="text-right">Líquido</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.student_name}</TableCell>
              <TableCell className="text-muted-foreground">
                {p.package_name}
              </TableCell>
              <TableCell className="text-muted-foreground whitespace-nowrap">
                {formatDate(p.paid_at)}
              </TableCell>
              <TableCell>
                <Badge variant="outline">
                  {methodLabel(p.payment_method, p.installment_count)}
                </Badge>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {formatCurrency(p.amount)}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                -{formatCurrency(p.fee_amount)}
                {p.fee_rate > 0 && (
                  <span className="block text-xs">
                    {p.fee_rate.toFixed(2).replace('.', ',')}%
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap font-semibold text-green-700 dark:text-green-500">
                {formatCurrency(p.net_amount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/** Quebra por forma de pagamento — responde "quanto entrou de PIX, de crédito". */
function MethodBreakdown({ rows }: { rows: MethodRow[] }) {
  if (rows.length === 0) return null

  return (
    <div className="rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Forma de pagamento</TableHead>
            <TableHead className="text-right">Vendas</TableHead>
            <TableHead className="text-right">Bruto</TableHead>
            <TableHead className="text-right">Taxa</TableHead>
            <TableHead className="text-right">Líquido</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.payment_method}>
              <TableCell className="font-medium">
                {METHOD_LABELS[row.payment_method] ?? row.payment_method}
              </TableCell>
              <TableCell className="text-right">
                {row.transactions_count}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {formatCurrency(row.gross_amount)}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                -{formatCurrency(row.fees_amount)}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap font-semibold text-green-700 dark:text-green-500">
                {formatCurrency(row.net_amount)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

/** Filtro de período. Recarrega só as props do relatório, preservando a aba. */
function PeriodFilter({ from, to }: { from: string; to: string }) {
  const [range, setRange] = useState({ from, to })

  function apply(next: { from: string; to: string }) {
    setRange(next)
    router.get('/payouts', next, {
      preserveState: true,
      preserveScroll: true,
      only: ['period_report'],
    })
  }

  function shiftMonth(offset: number) {
    const base = new Date()
    const target = new Date(base.getFullYear(), base.getMonth() + offset, 1)
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0)

    apply({
      from: format(target, 'yyyy-MM-dd'),
      to: format(last, 'yyyy-MM-dd'),
    })
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <label htmlFor="payout-from" className="text-xs text-muted-foreground">
          De
        </label>
        <Input
          id="payout-from"
          type="date"
          value={range.from}
          onChange={(e) => apply({ ...range, from: e.target.value })}
          className="w-40"
        />
      </div>
      <div className="space-y-1.5">
        <label htmlFor="payout-to" className="text-xs text-muted-foreground">
          Até
        </label>
        <Input
          id="payout-to"
          type="date"
          value={range.to}
          onChange={(e) => apply({ ...range, to: e.target.value })}
          className="w-40"
        />
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => shiftMonth(0)}>
          Este mês
        </Button>
        <Button variant="outline" size="sm" onClick={() => shiftMonth(-1)}>
          Mês passado
        </Button>
      </div>
    </div>
  )
}

// ── Página ──

export default function Index({
  school,
  next_payout,
  period_report,
  history,
  fee_table,
}: Props) {
  const periodLabel = `${formatDate(next_payout.period_start, 'dd/MM')} a ${formatDate(next_payout.period_end, 'dd/MM')}`

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-4 md:p-8 space-y-6">
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Repasses</h1>
            <p className="text-sm text-muted-foreground">
              Tudo que entrou pelo aplicativo, quanto você recebe e quando o
              dinheiro cai na sua conta.
            </p>
          </div>

          {/* A regra fica aqui, e nao so na aba "Como calculamos": e a primeira
              pergunta de qualquer escola olhando essa tela. */}
          <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3">
            <CalendarClock className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">
                O repasse é quinzenal.
              </span>{' '}
              As vendas dos dias 01 a 15 são pagas no dia 30; as do dia 16 até o
              fim do mês, no dia 15 do mês seguinte — sempre 15 dias após a
              quinzena fechar. Vale para PIX, débito e crédito: a forma de
              pagamento muda a taxa, não a data.
            </p>
          </div>
        </div>

        {/* ── Próximo repasse ── */}
        <div className="relative overflow-hidden rounded-xl shadow-md">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-emerald-700 to-teal-800" />
          <div className="relative p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-emerald-100" />
              <h2 className="text-sm font-semibold text-emerald-50 uppercase tracking-wide">
                Próximo repasse
              </h2>
            </div>

            <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
              <div>
                <p className="text-4xl font-bold text-white">
                  {formatCurrency(next_payout.net_amount)}
                </p>
                <p className="text-sm text-emerald-100 mt-1">
                  {next_payout.transactions_count}{' '}
                  {next_payout.transactions_count === 1 ? 'venda' : 'vendas'}
                  {' · '}quinzena {periodLabel}
                </p>
              </div>

              <div className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-3">
                <CalendarClock className="h-5 w-5 text-emerald-100" />
                <div>
                  <p className="text-xs text-emerald-100">
                    Cai na sua conta em
                  </p>
                  <p className="text-lg font-bold text-white">
                    {formatDate(next_payout.payout_date)}
                  </p>
                </div>
              </div>

              {/* Link direto (nao router.visit): o backend responde um arquivo,
                  e o Inertia nao sabe navegar para um download. */}
              <Button asChild variant="secondary" size="sm">
                <a href="/payouts/preview">
                  <Download className="h-4 w-4 mr-2" /> Baixar PDF
                </a>
              </Button>
            </div>

            {/* Bruto - taxas = líquido, para não restar dúvida de onde saiu o número */}
            <div className="flex flex-wrap items-center gap-2 text-sm text-emerald-50 pt-2 border-t border-white/20">
              <span>
                {formatCurrency(next_payout.gross_amount)} recebido dos alunos
              </span>
              <span className="text-emerald-200">−</span>
              <span>{formatCurrency(next_payout.fees_amount)} de taxas</span>
              <span className="text-emerald-200">=</span>
              <span className="font-semibold">
                {formatCurrency(next_payout.net_amount)} para você
              </span>
            </div>

            {/* Sem isto, uma venda de junho aparecia somada sob o rótulo da
                quinzena corrente e o total não batia com o extrato do banco. */}
            {next_payout.carried_over.transactions_count > 0 && (
              <p className="text-xs text-emerald-100/90">
                Inclui {formatCurrency(next_payout.carried_over.gross_amount)}{' '}
                de {next_payout.carried_over.transactions_count}{' '}
                {next_payout.carried_over.transactions_count === 1
                  ? 'venda anterior'
                  : 'vendas anteriores'}{' '}
                a esta quinzena, que ainda não foram repassadas.
              </p>
            )}
          </div>
        </div>

        {/* ── Detalhe / histórico / taxas ── */}
        <Tabs defaultValue="detail">
          <TabsList>
            <TabsTrigger value="detail">
              <Receipt className="h-4 w-4 mr-2" /> Extrato do próximo repasse
            </TabsTrigger>
            <TabsTrigger value="period">
              <CalendarSearch className="h-4 w-4 mr-2" /> Por período
            </TabsTrigger>
            <TabsTrigger value="history">
              <ArrowUpRight className="h-4 w-4 mr-2" /> Histórico
            </TabsTrigger>
            <TabsTrigger value="fees">
              <Info className="h-4 w-4 mr-2" /> Como calculamos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="detail" className="mt-6">
            <PaymentsTable payments={next_payout.payments} />
          </TabsContent>

          <TabsContent value="period" className="mt-6 space-y-6">
            <PeriodFilter from={period_report.from} to={period_report.to} />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: 'Vendas',
                  value: String(period_report.transactions_count),
                },
                {
                  label: 'Bruto',
                  value: formatCurrency(period_report.gross_amount),
                },
                {
                  label: 'Taxas',
                  value: `-${formatCurrency(period_report.fees_amount)}`,
                },
                {
                  label: 'Líquido',
                  value: formatCurrency(period_report.net_amount),
                },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border p-4">
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-xl font-bold tracking-tight">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Por forma de pagamento</h3>
              <MethodBreakdown rows={period_report.by_method} />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Vendas do período</h3>
              <PaymentsTable payments={period_report.payments} />
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            {history.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  Nenhuma quinzena fechada ainda. O primeiro repasse aparece
                  aqui depois do fechamento.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Período</TableHead>
                      <TableHead className="text-right">Pagamentos</TableHead>
                      <TableHead className="text-right">Bruto</TableHead>
                      <TableHead className="text-right">Taxas</TableHead>
                      <TableHead className="text-right">Líquido</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">
                        Demonstrativo
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="font-medium whitespace-nowrap">
                          {h.period_label}
                        </TableCell>
                        <TableCell className="text-right">
                          {h.transactions_count}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {formatCurrency(h.gross_amount)}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                          -{formatCurrency(h.fees_amount)}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap font-semibold text-green-700 dark:text-green-500">
                          {formatCurrency(h.net_amount)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {h.status === 'paid' ? (
                            <span className="inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-500">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Pago em{' '}
                              {formatDate(h.paid_at)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-sm text-amber-600">
                              <CalendarClock className="h-3.5 w-3.5" /> Previsto{' '}
                              {formatDate(h.payout_date)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button asChild variant="ghost" size="sm">
                            <a href={`/payouts/${h.id}`}>
                              <Download className="h-4 w-4 mr-1.5" /> PDF
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="fees" className="mt-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">
                  Taxa por forma de pagamento
                </h3>
                <PlatformFeesTable fees={fee_table} />
                <p className="text-xs text-muted-foreground">
                  Taxa aplicada sobre o valor bruto da transação. Pagamentos
                  registrados na escola (dinheiro, transferência) não passam
                  pelo gateway e não têm taxa.
                </p>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold">Quando o dinheiro cai</h3>
                <PlatformFeesExplainer />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}
